import { create } from 'zustand'

import type { ExampleKind } from '@repo/types'

/** VRG-138 Phase 1: a Vera package linked to an order or one of its line items. */
export interface LinkedPackageSummary {
  id: string;
  name: string;
}

/**
 * VRG-138 REDESIGN: a "shipment" — an `order_shipments` row grouping MANY
 * packages (one per grouped product) into ONE box-size preset. Mirrors the
 * API's `OrderShipment` (shopify-order.mapper.ts). `packages`/`lineItemIds`
 * are the grouped products; `status`/`tracking*`/`label*`/`weightOz`/
 * `boxPreset` are the shipment's own fulfillment state.
 */
export interface OrderShipment {
  id: string;
  packages: LinkedPackageSummary[];
  lineItemIds: string[];
  boxPreset?: string | null;
  status: 'ready' | 'shipped' | 'in_transit' | 'delivered';
  trackingNumber?: string | null;
  trackingUrl?: string | null;
  trackingCompany?: string | null;
  labelUrl?: string | null;
  labelStatus: 'none' | 'pending' | 'purchased' | 'failed';
  shopifyFulfillmentId?: string | null;
  weightOz?: string | null;
  fulfilledAt?: string | null;
}

export interface ShopifyOrder {
  id: string;
  name: string;
  createdAt: string;
  totalPriceSet: {
    shopMoney: {
      amount: string;
      currencyCode: string;
    };
  };
  /** VERA-891: the customer-facing shipping charge (Shopify's own field, not what Vera pays for the label). */
  shippingPriceSet?: {
    shopMoney: {
      amount: string;
      currencyCode: string;
    };
  };
  fulfillmentStatus: string;
  financialStatus: string;
  customer: {
    id?: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  shippingAddress: {
    address1: string;
    city: string;
    country: string;
    zip: string;
  };
  lineItems: Array<{
    id: string;
    title: string;
    quantity: number;
    variant: {
      id: string;
      sku: string;
      price: string;
      title: string;
    };
    /** VRG-138 Phase 1: packages linked to THIS line item specifically. */
    linkedPackages?: LinkedPackageSummary[];
    /** VERA-891: product image URL (Shopify line-item image; populated on both the list and detail fetches). */
    image?: string;
    /** §36: buyer's tag customization (logo/placement/finish) for fulfillment — null when not customized. */
    customization?: {
      frontLogoUrl?: string;
      backLogoUrl?: string;
      color?: string;
      finish?: string;
      logoFinish?: string;
    } | null;
  }>;
  tags: string[];
  note: string;
  trackingNumber: string;
  /** VERA-891: resolved company (via `companies.shopify_customer_id`), null when unmatched. */
  company?: { id: string; name: string } | null;
  /** VRG-138 Phase 1: packages linked to the ORDER as a whole (bulk header action, no line item). */
  orderLevelPackages?: LinkedPackageSummary[];
  /** VRG-138 Phase 2: this order's shipments (one package/box grouping ≥1 line items). */
  shipments?: OrderShipment[];
  /** VERA-891: line items with a package assigned (Orders list "Assignment" column, N/M). */
  assignedLineItemCount?: number;
  /** VERA-891: lightweight shipment summary for the Orders list kebab (Buy Label / Fulfill gating). */
  shipmentsSummary?: Array<{
    id: string;
    status: string;
    labelStatus: string;
    labelUrl?: string | null;
  }>;
  /**
   * VERA-894: single-order-detail-only tracking fields, sourced from the
   * order's first fulfillment. Absent (undefined) on list-mapped orders —
   * only the single-order GET (`useOrder`) populates them.
   */
  trackingCompany?: string;
  trackingUrl?: string;
  shipmentStatus?: string;
  estimatedDeliveryAt?: string | null;
  /** VERA-891: deep link to this order in the Shopify admin (Shopify owns order lifecycle). */
  adminUrl?: string;
  /**
   * VERA-891: the customer-facing Shopify invoice/checkout link
   * (`DraftOrder.invoiceUrl`). DRAFT ORDERS ONLY — the non-draft `Order` type
   * has no invoice URL, so real orders leave this null.
   */
  invoiceUrl?: string | null;
  /**
   * VRG-512 — `orders.example_kind`: seeded sample data, not a real order.
   * Renders the shared `ExampleKindBadge` in the pinned Order # column.
   *
   * Only ever non-null on the API's LOCAL-ORDERS FALLBACK path (a tenant with no
   * Shopify connection). Orders read from the live Shopify Admin API are real by
   * construction and always come back `null` — so on a dev machine whose Shopify
   * store has draft orders, the fallback never runs and no row is badged. See
   * `ShopifyOrder.example_kind` in `shopify-order.mapper.ts`.
   */
  example_kind?: ExampleKind | null;
}

interface OrdersUIState {
  selectedOrder: ShopifyOrder | null;
  openLinkPackageModal: boolean;
  currentPage: number;
  pageSize: number;
  setSelectedOrder: (order: ShopifyOrder | null) => void;
  setOpenLinkPackageModal: (open: boolean) => void;
  setCurrentPage: (page: number) => void;
  setPageSize: (size: number) => void;
}

export const useOrdersStore = create<OrdersUIState>((set) => ({
  selectedOrder: null,
  openLinkPackageModal: false,
  currentPage: 1,
  pageSize: 50,
  setSelectedOrder: (order) => set({ selectedOrder: order }),
  setOpenLinkPackageModal: (open) => set({ openLinkPackageModal: open }),
  setCurrentPage: (page) => set({ currentPage: page }),
  setPageSize: (size) => set({ pageSize: size }),
}))
